import React, { useState, useEffect } from 'react';
import styles from './ProtocolDetails.module.css';
import { updateProtocol, fetchProtocolCustomers } from '../services/protocolService';

const ProtocolDetails = ({ protocol, onBack, onDelete, onSave, hasUpdateOffers, hasDeleteOffers, hasShowCustomerOffer }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedData, setEditedData] = useState({
        title: protocol.title || '',
        companyName: protocol.title || '',
        description: protocol.description || '',
        image: protocol.image || null
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [customersError, setCustomersError] = useState(null);

    // Fetch customers when component mounts
    useEffect(() => {
        const fetchCustomers = async () => {
            setCustomersLoading(true);
            setCustomersError(null);
            try {
                const customersData = await fetchProtocolCustomers(protocol.id);
                setCustomers(customersData);
            } catch (err) {
                setCustomersError(err.message || 'Failed to fetch customers');
            } finally {
                setCustomersLoading(false);
            }
        };

        if (protocol.id) {
            fetchCustomers();
        }
    }, [protocol.id]);

    const handleFieldChange = (field, value) => {
        setEditedData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleImageUpload = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    setEditedData(prev => ({
                        ...prev,
                        image: {
                            id: Date.now(),
                            src: event.target.result,
                            alt: 'Protocol Image',
                            file: file
                        }
                    }));
                };
                reader.readAsDataURL(file);
            }
        };
        fileInput.click();
    };

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        try {
            await updateProtocol(protocol.id, {
                company_name: editedData.title,
                description: editedData.description,
                logo: editedData.image && editedData.image.file ? editedData.image.file : undefined
            });
            if (onSave) onSave({
                ...protocol,
                title: editedData.title,
                description: editedData.description,
                image: editedData.image && editedData.image.src ? editedData.image.src : editedData.image
            });
            setIsEditing(false);

            // Go back after successful save
            if (onBack) {
                onBack();
            }
        } catch (err) {
            setError(err.message || 'Failed to update protocol');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setEditedData({
            title: protocol.title || '',
            description: protocol.description || '',
            image: protocol.image || null
        });
        setIsEditing(false);
    };

    const handleDelete = () => {
        onDelete(protocol.id);
    };

    const currentData = isEditing ? editedData : protocol;

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.header}>
                    <div className={styles.backSection}>
                        <button className={styles.backButton} onClick={onBack}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5" />
                                <path d="M12 19l-7-7 7-7" />
                            </svg>
                            Back
                        </button>
                    </div>
                    <h2>Offer Details</h2>
                    <div className={styles.headerActions}>
                        {isEditing ? (
                            <>
                                <button className={styles.cancelButton} onClick={handleCancel}>
                                    Cancel
                                </button>
                                <button className={styles.saveButton} onClick={handleSave} disabled={loading}>
                                    {loading ? 'Saving...' : 'Save'}
                                </button>
                            </>
                        ) : (
                            <>
                                {hasUpdateOffers && (
                                    <button className={styles.editButton} onClick={() => setIsEditing(true)}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                        </svg>
                                        Edit
                                    </button>
                                )}
                                {hasDeleteOffers && (
                                    <button className={styles.deleteButton} onClick={handleDelete}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3,6 5,6 21,6" />
                                            <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
                                            <line x1="10" y1="11" x2="10" y2="17" />
                                            <line x1="14" y1="11" x2="14" y2="17" />
                                        </svg>
                                        Delete
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className={styles.mainContent}>
                    <div className={styles.leftContent}>
                        <div className={styles.formSection}>
                            <div className={styles.formGroup}>
                                <label>
                                    Company Name:
                                    {isEditing && <span className={styles.editIcon}>✏️</span>}
                                </label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={currentData.title}
                                        onChange={(e) => {
                                            handleFieldChange('title', e.target.value);
                                            handleFieldChange('companyName', e.target.value);
                                        }}
                                        className={styles.input}
                                    />
                                ) : (
                                    <div className={styles.title}>{currentData.title}</div>
                                )}
                            </div>

                            <div className={styles.formGroup}>
                                <label>
                                    Description:
                                    {isEditing && <span className={styles.editIcon}>✏️</span>}
                                </label>
                                {isEditing ? (
                                    <textarea
                                        value={currentData.description}
                                        onChange={(e) => handleFieldChange('description', e.target.value)}
                                        className={styles.textarea}
                                        rows="6"
                                    />
                                ) : (
                                    <div className={styles.description}>{currentData.description}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={styles.rightContent}>
                        <div className={styles.imageSection}>
                            <h3>Offer Image</h3>
                            {currentData.image ? (
                                <div className={styles.imageContainer}>
                                    <img src={currentData.image.src || currentData.image} alt="Offer" />
                                    {isEditing && (
                                        <div className={styles.imageActions}>

                                            <button
                                                className={styles.removeButton}
                                                onClick={() => setEditedData(prev => ({ ...prev, image: null }))}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className={styles.uploadContainer}>
                                    <div className={styles.uploadIcon}>
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="7,10 12,15 17,10" />
                                            <line x1="12" y1="15" x2="12" y2="3" />
                                        </svg>
                                    </div>
                                    <p className={styles.uploadText}>Upload Protocol Image</p>
                                    {isEditing && (
                                        <button className={styles.uploadButton} onClick={handleImageUpload}>
                                            Choose File
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Customers Section */}
                {hasShowCustomerOffer && (
                    <div className={styles.customersTableSection}>
                        <div className={styles.customersHeader}>
                            <div className={styles.customersHeaderLeft}>
                                <svg className={styles.customersIcon} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                                <h3>Customers Who Enjoy This Offer</h3>
                            </div>

                        </div>

                        {customersLoading ? (
                            <div className={styles.loadingState}>
                                <div className={styles.spinner}></div>
                                <p>Loading customers...</p>
                            </div>
                        ) : customersError ? (
                            <div className={styles.errorState}>
                                <svg className={styles.errorIcon} width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="15" y1="9" x2="9" y2="15" />
                                    <line x1="9" y1="9" x2="15" y2="15" />
                                </svg>
                                <h4>Failed to Load Customers</h4>
                                <p>{customersError}</p>
                                <button
                                    className={styles.retryButton}
                                    onClick={() => {
                                        setCustomersError(null);
                                        const fetchCustomers = async () => {
                                            setCustomersLoading(true);
                                            try {
                                                const customersData = await fetchProtocolCustomers(protocol.id);
                                                setCustomers(customersData);
                                            } catch (err) {
                                                setCustomersError(err.message || 'Failed to fetch customers');
                                            } finally {
                                                setCustomersLoading(false);
                                            }
                                        };
                                        fetchCustomers();
                                    }}
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : customers.length === 0 ? (
                            <div className={styles.emptyState}>
                                <svg className={styles.emptyIcon} width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                                <h4>No Customers Yet</h4>
                                <p>No customers have enjoyed this offer yet. Share this offer to attract more customers!</p>
                            </div>
                        ) : (
                            <div className={styles.tableContainer}>
                                <table className={styles.customersTable}>
                                    <thead>
                                        <tr>
                                            <th>
                                                <div className={styles.tableHeader}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                                        <circle cx="12" cy="7" r="4" />
                                                    </svg>
                                                    Name
                                                </div>
                                            </th>
                                            <th>
                                                <div className={styles.tableHeader}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                                        <polyline points="22,6 12,13 2,6" />
                                                    </svg>
                                                    Email
                                                </div>
                                            </th>
                                            <th>
                                                <div className={styles.tableHeader}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                                    </svg>
                                                    Phone Number
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customers.map((customer, idx) => (
                                            <tr key={idx} className={styles.customerRow}>
                                                <td>
                                                    <div className={styles.customerCell}>
                                                        <div className={styles.customerAvatar}>
                                                            {customer.name?.charAt(0)?.toUpperCase() || 'C'}
                                                        </div>
                                                        <span className={styles.customerName}>{customer.name || 'Unknown Customer'}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={styles.customerEmail}>{customer.email || 'No email provided'}</span>
                                                </td>
                                                <td>
                                                    <div className={styles.phoneCell}>
                                                        <span className={styles.phoneCode}>{customer.phone_code}</span>
                                                        <span className={styles.phoneNumber}>{customer.phone}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
                {isEditing && error && <div className={styles.error}>{error}</div>}
            </div>
        </div>
    );
};

export default ProtocolDetails; 